import { createContext, useEffect, useContext, useMemo, useReducer } from "react";
import { FieldObfsMappingDto, ObfuscationRuleDto, TableObfsMappingCountDto } from "~/gen/api/encrypt";


interface States {
  databaseId: number,
  currTable: TableObfsMappingCountDto|null,
  mappingEdits: Record<number, number|'rm_map'>,
}
type Action =
| { type: 'switch_database', payload: number }
| { type: 'switch_table',    payload: TableObfsMappingCountDto|null }
| { type: 'set_field_obfs_rule', payload: { field: FieldObfsMappingDto, rule: ObfuscationRuleDto|'rm_map'} }
| { type: 'clear_mappings' }
| { type: 'clear_field_mapping', payload: { field: FieldObfsMappingDto } }


interface Handlers {
  switchDatabase: (x:number) => void,
  switchTable: (table: TableObfsMappingCountDto|null) => void,
  setFieldObfsRul: (field: FieldObfsMappingDto, rule: ObfuscationRuleDto|'rm_map') => void,
  clearMappings: () => void,
  clearFieldMapping: (field: FieldObfsMappingDto) => void,
}


const reducer = (prevStates: States, action: Action): States => {
  switch (action.type) {
    case 'switch_database': {
      if (prevStates.databaseId === action.payload) { return prevStates }

      return { ...prevStates, databaseId: action.payload, currTable: null, mappingEdits: {} }
    }
    case 'switch_table': {
      if (prevStates.currTable?.tableId === action.payload?.tableId) {
        return prevStates
      }
      const confirmed = Object.keys(prevStates.mappingEdits).length === 0 ||
        window.confirm('You have unsaved mapping edits, do you wish to discard the changes and switch table?')

      return confirmed ? { ...prevStates, currTable: action.payload, mappingEdits: {} } : prevStates
    }
    case 'set_field_obfs_rule': {
      const { field, rule } = action.payload
      const fieldId = field.fieldId || 0
      if (rule === 'rm_map') {
        if (field.obfuscationRuleId) {
          return { ...prevStates, mappingEdits: { ...prevStates.mappingEdits, [fieldId]: 'rm_map' }}
        } else if (prevStates.mappingEdits[fieldId]) {
          const removed = { ...prevStates.mappingEdits }
          delete removed[fieldId]
          return { ...prevStates, mappingEdits: removed }
        } else {
          return prevStates
        }
      } else {
        if (field.obfuscationRuleId !== rule.ruleId) { // edit to other rule
          return { ...prevStates, mappingEdits: { ...prevStates.mappingEdits, [fieldId]: rule.ruleId || 0}}
        } else if (prevStates.mappingEdits[fieldId]) { // edit back to original rule
          const bag = prevStates.mappingEdits;
          delete bag[fieldId]
          return { ...prevStates, mappingEdits: bag }
        } else {
          return prevStates
        }
      }
    }
    case 'clear_mappings': {
      return { ...prevStates, mappingEdits: {} }
    }
    case 'clear_field_mapping': {
      const {field} = action.payload
      const bag = {...prevStates.mappingEdits}
      delete bag[field.fieldId!]
      return { ...prevStates, mappingEdits: bag }
    }
  }
}


export const useEditObfsMappingController = (databaseId: number): EditObfsMappingContextProps => {
  const [states, dispatch] = useReducer(reducer, {
    databaseId: 0,
    currTable: null,
    mappingEdits: {},
  })

  const handlers = useMemo(() => ({
    switchDatabase: (x:number) => 
      dispatch({ type: 'switch_database', payload: x }),

    switchTable: (table: TableObfsMappingCountDto|null) => 
      dispatch({ type: 'switch_table', payload: table }),

    setFieldObfsRul: (field: FieldObfsMappingDto, rule: ObfuscationRuleDto|'rm_map') => 
      dispatch({ type: 'set_field_obfs_rule', payload: { field, rule }}),

    clearMappings: () => 
      dispatch({ type: 'clear_mappings' }),
    
    clearFieldMapping: (field: FieldObfsMappingDto) => 
      dispatch({ type: 'clear_field_mapping', payload: {field} }),

  }), [dispatch])

  useEffect(() => {
    handlers.switchDatabase(databaseId)
  }, [databaseId, handlers])

  return useMemo(() => ({
    ...states,
    ...handlers,
  }), [states, handlers])
}

export interface EditObfsMappingContextProps extends States, Handlers {}

const EditObfsMappingContext = createContext<EditObfsMappingContextProps>({} as EditObfsMappingContextProps)

export const EditObfsMappingContextProvider = EditObfsMappingContext.Provider

export const useEditObfsMappingContext = () => useContext(EditObfsMappingContext)
